import find from 'lodash/find.js';
import flatMap from 'lodash/flatMap.js';
import get from 'lodash/get.js';
import map from 'lodash/map.js';
import unionBy from 'lodash/unionBy.js';

import { initialState } from '@/Helpers.js';
import Course from '@/models/Course.js';
import CourseSettings from '@/models/CourseSettings.js';
import Model from '@/models/Model.js';
import ModelRole from '@/models/ModelRole.js';
import { ADMIN, INSTRUCTION_ADMIN, INSTRUCTOR, OWNER, STUDENT_ROLE_NAME } from '@/models/Role.js';
import moment from '@/plugins/vue-moment.js';

export default class CourseEnrollment extends Model {
    static entity = 'course_enrollments';

    static fields() {
        return {
            id: this.attr(null),
            course_id: this.attr(null),
            user_id: this.attr(null),
            course_key_id: this.attr(null),
            activity_submissions: this.attr(null),
            deleted_at: this.attr(null),
            individual_start_date: this.attr(null),

            ...super.fields(),

            course: this.belongsTo('courses', 'course_id'),
            user: this.belongsTo('users', 'user_id'),
            meeting_participant_comments: this.hasMany('meeting_participant_comments', 'course_enrollment_id'),
            roles: this.morphToMany('roles', 'model_has_roles', 'role_id', 'model_id', 'model_type'),
            statuses: this.morphMany('statuses', 'model_id', 'model_type_entity'),
            enrollment_groups: this.belongsToMany(
                'enrollment_groups',
                'course_enrollment_enrollment_group',
                'course_enrollment_id',
                'enrollment_group_id'
            ),
        };
    }

    get is_student() {
        return map(this.roles, 'name').some(name => name === STUDENT_ROLE_NAME);
    }

    get all_permissions() {
        return unionBy(
            flatMap(this.roles, role => role.permissions),
            'id'
        );
    }

    get has_disabled_status() {
        return (
            this.statuses.filter(status => initialState('AccountStatuses.disabling').includes(status.name)).length > 0
        );
    }

    get has_removed_status() {
        return this.statuses.filter(status => initialState('AccountStatuses.removed').includes(status.name)).length > 0;
    }

    hasPermissionTo(permission) {
        return !!find(this.all_permissions, { name: permission });
    }

    get has_group() {
        return this.enrollment_groups.length > 0;
    }

    group(attribute, defaultValue) {
        return get(this, `enrollment_groups[0].${attribute}`, defaultValue);
    }

    get name() {
        return this.user.name;
    }

    get photo_url() {
        return this.user.photo_url;
    }

    static searchable() {
        return ['name'];
    }

    canAccessReportsPage() {
        return map(this.roles, 'id').some(roleId => [OWNER, ADMIN, INSTRUCTION_ADMIN, INSTRUCTOR].includes(roleId));
    }

    canAccessAllReports() {
        return map(this.roles, 'id').some(roleId => [OWNER, ADMIN, INSTRUCTION_ADMIN].includes(roleId));
    }

    removedBeforeStartDate(courseOrStartDate = null) {
        let startDate;

        if (courseOrStartDate instanceof Date) {
            startDate = moment.utc(courseOrStartDate);
        }

        if (courseOrStartDate instanceof Course) {
            startDate = courseOrStartDate.course_settings.timestamp('start_date');
        }

        if (courseOrStartDate instanceof CourseSettings) {
            startDate = courseOrStartDate.timestamp('start_date');
        }

        if (!startDate) {
            startDate = this.course.course_settings.timestamp('start_date');
        }

        if (!startDate) {
            throw new Error('Course start date is not defined');
        }

        // Check if the deleted_at date is before the course start date
        return this.deleted_at && moment.utc(this.deleted_at).isBefore(startDate);
    }

    hasInstructorRole() {
        return map(this.roles, 'id').includes(INSTRUCTOR);
    }

    updateRoles(selectedRoles) {
        ModelRole.query()
            .where('model_id', this.id)
            .where('model_type', 'course_enrollments')
            .get()
            .forEach(modelRole => modelRole.$delete());

        return CourseEnrollment.api().put(`/api/course-enrollments/${this.id}`, {
            ...this,
            roles: map(selectedRoles, 'id'),
        });
    }
}
